/**
 * IdCardUpload — rectangular ID-card image control (front / back). Thin
 * wrapper over the shared `ImageUploadField` (dialog-based view / change /
 * remove), matching Flutter `personal_details_widget.dart` ID-card tiles +
 * `upload_image_dialog.dart` (dialog title "Change front id card image").
 *
 * Wire (PUT-curl-verified 2026-06-04): the patient save body carries the
 * hosted URL on `frontIdCardUrl` / `backIdCardUrl`.
 */

import { ImageUploadField } from './image-upload-field';

export interface IdCardUploadProps {
  /** Hosted image URL (or null/empty when unset). */
  url: string | null;
  onChange: (next: string | null) => void;
  /** Visible label, e.g. "Front" / "Back". */
  label: string;
  disabled?: boolean;
  /** Suppress the "ID card — {label}" header (used when the parent section
   *  already carries the "ID CARD" heading — Flutter ID-card tiles sit under
   *  a single bare "ID CARD" label). */
  hideHeaderLabel?: boolean;
  /** Centered empty-state text. Defaults to "Upload {label}". Flutter uses
   *  "Scan front side of ID" / "Scan back side of ID". */
  emptyText?: string;
}

export function IdCardUpload({
  url,
  onChange,
  label,
  disabled = false,
  hideHeaderLabel = false,
  emptyText,
}: IdCardUploadProps) {
  return (
    <div>
      {hideHeaderLabel ? null : (
        <span className="idc-label">
          ID card — {label}
        </span>
      )}
      <div className="idc-field">
        <ImageUploadField
          url={url}
          onChange={onChange}
          disabled={disabled}
          variant="idcard"
          dialogTitle={`Change ${label.toLowerCase()} ID card image`}
          emptyText={emptyText ?? `Upload ${label}`}
          altText={`ID card ${label}`}
          allowRemove
        />
      </div>
    </div>
  );
}
